import { Injectable } from '@angular/core';
import { latLng, LatLng } from 'leaflet';
import { Observable, BehaviorSubject, Subject } from 'rxjs';

export enum GpsQuality { Accurate = 0, Slow, Bad, BadAndSlow, None = -1 }
export type HeadingSource = 'compass' | 'gps' | null;

@Injectable({
	providedIn: 'root'
})
export class GpsService {

	private watchId?: number;
	private slowTimer?: ReturnType<typeof setInterval>;
	private _gps = new BehaviorSubject<GpsQuality>(GpsQuality.None);
	private _position = new Subject<LatLng>();
	private _heading = new BehaviorSubject<number | null>(null);
	private _headingSource = new BehaviorSubject<HeadingSource>(null);
	private lastGpsHeading: number | null = null;
	private hasCompass = false;
	private lastPositionTime = 0;

	constructor() {
		const options: PositionOptions = {
			enableHighAccuracy: true,
			timeout: 10000,
			maximumAge: 0,
		};

		const success = (pos: GeolocationPosition) => {
			this.lastPositionTime = Date.now();
			let next = this.addGpsFlag(GpsQuality.Accurate, false, this._gps.getValue());
			if (pos.coords.accuracy > 20) next = this.addGpsFlag(GpsQuality.Bad, false, next);
			else next = this.removeGpsFlag(GpsQuality.Bad, false, next);
			next = this.removeGpsFlag(GpsQuality.Slow, false, next);
			this._gps.next(next);

			// GPS-Heading nur verwenden wenn kein Kompass verfügbar
			if (pos.coords.heading != null && !this.hasCompass) {
				this.lastGpsHeading = pos.coords.heading;
				this._heading.next(pos.coords.heading);
				this._headingSource.next('gps');
			}

			this._position.next(latLng([pos.coords.latitude, pos.coords.longitude]));
		};

		const error = () => {
			this._gps.next(GpsQuality.None);
		}

		this.watchId = navigator.geolocation.watchPosition(success, error, options);

		// Fallback: Status auf "Slow" setzen wenn länger als 5s kein Event kommt
		this.slowTimer = setInterval(() => {
			if (this.lastPositionTime > 0 && Date.now() - this.lastPositionTime > 5000) {
				this.addGpsFlag(GpsQuality.Slow);
			}
		}, 5000);

		// Device Orientation API für Kompass (Android)
		this.initCompass();
	}

	private initCompass(): void {
		/**
		 * Berechnet den Kompass-Heading aus den Device-Orientation-Werten.
		 *
		 * Verwendet den Rotation-Matrix-Ansatz aus der W3C Device Orientation Spec:
		 * Die Y-Achse des Geräts (Oberkante in natürlicher Ausrichtung) wird auf die
		 * Horizontalebene projiziert und der Winkel zu Nord berechnet.
		 *
		 * Vorteile gegenüber einfacher Alpha-Invertierung:
		 * - Funktioniert korrekt bei jeder Geräteneigung
		 * - Kein separater "über Kopf"-Hack nötig
		 * - Mathematisch exakte Projektion auf die Horizontalebene
		 *
		 * Quelle: https://w3c.github.io/deviceorientation/#worked-example-2
		 */
		const calculateHeading = (alpha: number, beta: number, gamma: number): number => {
			const degToRad = Math.PI / 180;
			const cA = Math.cos(alpha * degToRad);
			const sA = Math.sin(alpha * degToRad);
			const sB = Math.sin(beta * degToRad);
			const cG = Math.cos(gamma * degToRad);
			const sG = Math.sin(gamma * degToRad);

			// Projektion der Geräte-Y-Achse auf die Horizontalebene
			const rA = -cA * sG - sA * sB * cG;
			const rB = -sA * sG + cA * sB * cG;

			let heading = Math.atan2(rA, rB);
			if (heading < 0) heading += 2 * Math.PI;
			heading = heading * (180 / Math.PI);

			// Screen-Orientierung kompensieren
			let screenAngle = 0;
			if (screen.orientation?.angle !== undefined) {
				screenAngle = screen.orientation.angle;
			} else if ((window as any).orientation !== undefined) {
				screenAngle = (window as any).orientation;
				if (screenAngle < 0) screenAngle += 360;
			}
			heading = (heading + screenAngle) % 360;

			return heading;
		};

		// Gemeinsamer Handler für alle Orientation-Events
		const handleOrientation = (event: DeviceOrientationEvent) => {
			// iOS: webkitCompassHeading ist bereits korrekt berechnet
			// und berücksichtigt Screen-Orientierung und Gerätehaltung
			if ((event as any).webkitCompassHeading != null) {
				const accuracy = (event as any).webkitCompassAccuracy;
				// Nur verwenden wenn Genauigkeit akzeptabel (< 50°) oder unbekannt
				if (accuracy == null || accuracy < 0 || accuracy < 50) {
					const heading = (event as any).webkitCompassHeading as number;
					this.hasCompass = true;
					this._heading.next(heading);
					this._headingSource.next('compass');
				}
				return;
			}

			// Android/Standard: Heading aus alpha/beta/gamma berechnen
			if (event.alpha != null && event.beta != null && event.gamma != null) {
				const heading = calculateHeading(event.alpha, event.beta, event.gamma);
				this.hasCompass = true;
				this._heading.next(heading);
				this._headingSource.next('compass');
			}
		};

		const win = window as any;

		// iOS: Permission muss bei User-Interaktion angefordert werden
		if (typeof DeviceOrientationEvent !== 'undefined' &&
			typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
			// iOS 13+ - Listener registrieren, Permission später anfordern
			win.addEventListener('deviceorientation', handleOrientation as EventListener, true);
		} else if (win.ondeviceorientationabsolute !== undefined) {
			// Android: deviceorientationabsolute bevorzugt (liefert absolute Werte)
			win.addEventListener('deviceorientationabsolute', handleOrientation as EventListener, true);
		} else if (win.ondeviceorientation !== undefined) {
			// Fallback
			win.addEventListener('deviceorientation', handleOrientation as EventListener, true);
		}
	}

	/**
	 * Fordert Kompass-Permission an (iOS 13+).
	 * Muss bei User-Interaktion aufgerufen werden (z.B. Button-Klick).
	 */
	async requestCompassPermission(): Promise<boolean> {
		if (typeof DeviceOrientationEvent !== 'undefined' &&
			typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
			try {
				const permission = await (DeviceOrientationEvent as any).requestPermission();
				return permission === 'granted';
			} catch {
				return false;
			}
		}
		// Keine Permission nötig
		return true;
	}

	private addGpsFlag(q: GpsQuality, immediate = true, current = GpsQuality.None) {
		const cur = immediate ? this._gps.getValue() : current;
		const next = cur == GpsQuality.None ? q : cur | q;
		if (immediate) this._gps.next(next);
		return next;
	}

	private removeGpsFlag(q: GpsQuality, immediate = true, current = GpsQuality.None) {
		const cur = immediate ? this._gps.getValue() : current;
		const next = cur == GpsQuality.None ? GpsQuality.None : cur & ~q;
		if (immediate) this._gps.next(next);
		return next;
	}

	monitor(): Observable<GpsQuality> {
		return this._gps;
	}

	position() {
		return this._position;
	}

	heading(): Observable<number | null> {
		return this._heading;
	}

	headingSource(): Observable<HeadingSource> {
		return this._headingSource;
	}
}
